import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Eraser, Redo2, Undo2 } from "lucide-react";
import { Button, useToast } from "../../ui/kit";
import { hashSeed } from "../../../shared/rng";
import type { BoardProps } from "../types";
import { specFor } from "../catalog";
import { analyze, boundsOf, cellKey, hEdge, partitionKey, regionSymmetries, vEdge, type Cell } from "./polyomino";
import type { CutSpec } from "./generate";

/** soft "pond" colours; pieces with the same shape get the same colour */
const COLORS = ["#e9724c", "#f3c84f", "#79a86f", "#f2a7a0", "#7fb7d4", "#b7a4d6", "#e2a35d", "#8ac5b5"];

function sanitize(saved: unknown, spec: CutSpec): string[] {
  const allowed = new Set<string>();
  const set = new Set(spec.cells.map(([x, y]) => cellKey(x, y)));
  for (const [x, y] of spec.cells) {
    if (set.has(cellKey(x - 1, y))) allowed.add(vEdge(x, y));
    if (set.has(cellKey(x, y - 1))) allowed.add(hEdge(x, y));
  }
  const cuts = (saved as { cuts?: unknown } | null)?.cuts;
  return Array.isArray(cuts) ? cuts.filter((c): c is string => typeof c === "string" && allowed.has(c)) : [];
}

/** how much of each end of an edge is "dead": near a corner the pointer belongs to no edge, so passing a corner never cuts by accident */
const END_MARGIN = 0.16;

/**
 * The edge under a point (in cell units), if any. `orient` says which way the pointer has been travelling: a drag
 * that runs along a vertical line only picks vertical edges, so wobbling near a crossing doesn't cut sideways.
 */
function sanitizeFound(saved: unknown): string[] {
  const f = (saved as { found?: unknown } | null)?.found;
  return Array.isArray(f) ? f.filter((k): k is string => typeof k === "string" && k.length <= 16).slice(0, 40) : [];
}

function edgeAt(px: number, py: number, region: ReadonlySet<string>, tol: number, orient: "v" | "h" | null): string | null {
  const vx = Math.round(px);
  const hy = Math.round(py);
  const dv = Math.abs(px - vx);
  const dh = Math.abs(py - hy);
  const cy = Math.floor(py);
  const cx = Math.floor(px);
  const ty = py - cy;
  const tx = px - cx;
  let best: { d: number; e: string } | null = null;
  if (orient !== "h" && dv <= tol && ty >= END_MARGIN && ty <= 1 - END_MARGIN && region.has(cellKey(vx - 1, cy)) && region.has(cellKey(vx, cy))) {
    best = { d: dv, e: vEdge(vx, cy) };
  }
  if (orient !== "v" && dh <= tol && tx >= END_MARGIN && tx <= 1 - END_MARGIN && region.has(cellKey(cx, hy - 1)) && region.has(cellKey(cx, hy)) && (!best || dh < best.d)) {
    best = { d: dh, e: hEdge(cx, hy) };
  }
  return best ? best.e : null;
}

const segOf = (e: string): [number, number, number, number] => {
  const [xs, ys] = e.slice(1).split(",").map(Number);
  return e[0] === "v" ? [xs, ys, xs, ys + 1] : [xs, ys, xs + 1, ys];
};

export default function CutBoard({ id, saved, hintLevel, showAnswer, restartToken, onState, onSolved }: BoardProps) {
  const spec = (specFor(id) as { spec: CutSpec }).spec;
  const region = useMemo(() => new Set(spec.cells.map(([x, y]) => cellKey(x, y))), [spec]);
  const b = useMemo(() => boundsOf(spec.cells), [spec]);
  const toast = useToast();
  const syms = useMemo(() => regionSymmetries(spec.cells), [spec]);
  const [cuts, setCuts] = useState<string[]>(() => sanitize(saved, spec));
  // the really different ways this player has found so far (a short fingerprint of each)
  const [found, setFound] = useState<string[]>(() => sanitizeFound(saved));
  const foundRef = useRef(found);
  foundRef.current = found;
  const past = useRef<string[][]>([]);
  const future = useRef<string[][]>([]);
  const [, bump] = useState(0);
  const [hover, setHover] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<{ mode: "add" | "remove"; last: { x: number; y: number }; before: string[]; touched: boolean; ax: number; ay: number } | null>(null);
  const cutsRef = useRef(cuts);
  cutsRef.current = cuts;
  // a puzzle that was already solved when the page opened must not "solve" again
  const solvedRef = useRef<boolean | null>(null);
  if (solvedRef.current === null) solvedRef.current = analyze(spec.cells, new Set(cuts), spec.pieces).solved;

  const cutSet = useMemo(() => new Set(cuts), [cuts]);
  const analysis = useMemo(() => analyze(spec.cells, cutSet, spec.pieces), [spec, cutSet]);

  // restart
  const lastToken = useRef(restartToken);
  useEffect(() => {
    if (lastToken.current === restartToken) return;
    lastToken.current = restartToken;
    past.current = [];
    future.current = [];
    solvedRef.current = false;
    setCuts([]);
    onState({ cuts: [], found: foundRef.current });
  }, [restartToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = useCallback((next: string[]) => {
    setCuts(next);
    onState({ cuts: next, found: foundRef.current });
  }, [onState]);

  // solved? and is it a way we haven't seen before?
  const seenSolvedOnce = useRef(false);
  useEffect(() => {
    const userDriven = seenSolvedOnce.current;
    seenSolvedOnce.current = true;
    if (analysis.solved) {
      const key = hashSeed(partitionKey(spec.cells, analysis.pieces.map((p) => p.cells), syms)).toString(36);
      if (!foundRef.current.includes(key)) {
        const nextFound = [...foundRef.current, key];
        foundRef.current = nextFound;
        setFound(nextFound);
        onState({ cuts: cutsRef.current, found: nextFound });
        if (userDriven && nextFound.length > 1) {
          toast(nextFound.length >= spec.ways ? `You found every way: all ${spec.ways} of them!` : `A new way! ${nextFound.length} of ${spec.ways} found.`);
        }
      } else if (userDriven && !solvedRef.current) {
        toast("Nice, but you found that way already. Look for a different one.");
      }
      if (!solvedRef.current) {
        solvedRef.current = true;
        onSolved(`${spec.pieces} pieces`);
      }
    } else {
      solvedRef.current = false;
    }
  }, [analysis.solved, analysis.pieces, spec, syms, onSolved, onState, toast]);

  // ---- keyboard: a cursor square; arrows move it, Shift+arrows cut or uncut the wall beside it ----
  const [cursor, setCursor] = useState<Cell | null>(null);
  const [kbdNote, setKbdNote] = useState("");
  const showCursor = cursor !== null;
  const toggleWall = (e: string) => {
    const before = cutsRef.current;
    const next = before.includes(e) ? before.filter((c) => c !== e) : [...before, e];
    past.current.push(before);
    future.current = [];
    cutsRef.current = next;
    commit(next);
    bump((n) => n + 1);
    setKbdNote(before.includes(e) ? "Wall removed." : "Wall cut.");
  };
  const onKey = (ev: React.KeyboardEvent<SVGSVGElement>) => {
    const cur: Cell = cursor ?? spec.cells[0];
    const dirs: Record<string, [number, number]> = { ArrowRight: [1, 0], ArrowLeft: [-1, 0], ArrowDown: [0, 1], ArrowUp: [0, -1] };
    const d = dirs[ev.key];
    if (d) {
      ev.preventDefault();
      const target: Cell = [cur[0] + d[0], cur[1] + d[1]];
      if (ev.shiftKey) {
        // the wall on that side of the cursor square, if there is a square on the other side too
        if (!region.has(cellKey(target[0], target[1]))) return void setKbdNote("That is the edge of the shape.");
        const edge = d[0] === 1 ? vEdge(cur[0] + 1, cur[1]) : d[0] === -1 ? vEdge(cur[0], cur[1]) : d[1] === 1 ? hEdge(cur[0], cur[1] + 1) : hEdge(cur[0], cur[1]);
        toggleWall(edge);
      } else if (region.has(cellKey(target[0], target[1]))) {
        setCursor(target);
        const info = pieceOfCell.get(cellKey(target[0], target[1]));
        setKbdNote(`Square ${target[0] + 1} across, ${target[1] + 1} down${info && n > 1 ? `, in a piece of ${info.size}` : ""}.`);
      } else {
        setKbdNote("That is the edge of the shape.");
      }
    } else if (ev.key === "Backspace" || ev.key === "Delete") {
      ev.preventDefault();
      undo();
      setKbdNote("Undid the last cut.");
    }
  };

  const toPoint = (e: React.PointerEvent): { x: number; y: number } => {
    const svg = svgRef.current!;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    return { x: p.x, y: p.y };
  };

  const applyAlong = (from: { x: number; y: number }, to: { x: number; y: number }, tol: number) => {
    const d = drag.current!;
    // a smoothed sense of direction, so tiny jitters don't flip between "vertical" and "horizontal"
    d.ax = d.ax * 0.6 + (to.x - from.x);
    d.ay = d.ay * 0.6 + (to.y - from.y);
    const orient: "v" | "h" | null = Math.abs(d.ay) > 1.7 * Math.abs(d.ax) ? "v" : Math.abs(d.ax) > 1.7 * Math.abs(d.ay) ? "h" : null;
    const steps = Math.max(1, Math.ceil(Math.hypot(to.x - from.x, to.y - from.y) / 0.1));
    let next = cutsRef.current;
    for (let i = 1; i <= steps; i++) {
      const x = from.x + ((to.x - from.x) * i) / steps;
      const y = from.y + ((to.y - from.y) * i) / steps;
      const e = edgeAt(x, y, region, tol, orient);
      if (!e) continue;
      if (!d.touched) {
        d.touched = true;
        d.mode = next.includes(e) ? "remove" : "add";
      }
      if (d.mode === "add" && !next.includes(e)) next = [...next, e];
      if (d.mode === "remove" && next.includes(e)) next = next.filter((c) => c !== e);
    }
    if (next !== cutsRef.current) {
      cutsRef.current = next;
      setCuts(next);
    }
  };

  const onDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    const p = toPoint(e);
    drag.current = { mode: "add", last: p, before: cutsRef.current, touched: false, ax: 0, ay: 0 };
    applyAlong(p, p, e.pointerType === "touch" ? 0.42 : 0.34);
    e.preventDefault();
  };
  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const p = toPoint(e);
    const tol = e.pointerType === "touch" ? 0.42 : 0.34;
    if (drag.current) {
      applyAlong(drag.current.last, p, tol);
      drag.current.last = p;
    } else if (e.pointerType === "mouse") {
      setHover(edgeAt(p.x, p.y, region, tol, null));
    }
  };
  const onUp = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    const now = cutsRef.current;
    if (now !== d.before && JSON.stringify([...now].sort()) !== JSON.stringify([...d.before].sort())) {
      past.current.push(d.before);
      future.current = [];
      commit(now);
      bump((n) => n + 1);
    }
  };

  const undo = () => {
    const prev = past.current.pop();
    if (!prev) return;
    future.current.push(cutsRef.current);
    commit(prev);
    bump((n) => n + 1);
  };
  const redo = () => {
    const nxt = future.current.pop();
    if (!nxt) return;
    past.current.push(cutsRef.current);
    commit(nxt);
    bump((n) => n + 1);
  };
  const clear = () => {
    if (!cutsRef.current.length) return;
    past.current.push(cutsRef.current);
    future.current = [];
    commit([]);
    bump((n) => n + 1);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // ---- drawing helpers ----
  const pieceOfCell = useMemo(() => {
    const m = new Map<string, { cls: number; size: number; idx: number }>();
    analysis.pieces.forEach((p, idx) => p.cells.forEach(([x, y]) => m.set(cellKey(x, y), { cls: p.cls, size: p.cells.length, idx })));
    return m;
  }, [analysis]);

  const boundary: string[] = [];
  const inner: string[] = [];
  for (const [x, y] of spec.cells) {
    if (!region.has(cellKey(x, y - 1))) boundary.push(hEdge(x, y));
    else inner.push(hEdge(x, y));
    if (!region.has(cellKey(x - 1, y))) boundary.push(vEdge(x, y));
    else inner.push(vEdge(x, y));
    if (!region.has(cellKey(x, y + 1))) boundary.push(hEdge(x, y + 1));
    if (!region.has(cellKey(x + 1, y))) boundary.push(vEdge(x + 1, y));
  }
  const pad = 0.7;
  const vb = `${b.minX - pad} ${b.minY - pad} ${b.w + pad * 2} ${b.h + pad * 2}`;
  const answerSet = useMemo(() => new Set(spec.solution), [spec]);

  // where to put a size label on each separated piece: its cell closest to the centroid
  const labels = useMemo(() => {
    if (analysis.pieces.length < 2) return [];
    return analysis.pieces.map((p) => {
      const cx = p.cells.reduce((s, c) => s + c[0] + 0.5, 0) / p.cells.length;
      const cy = p.cells.reduce((s, c) => s + c[1] + 0.5, 0) / p.cells.length;
      let best: Cell = p.cells[0];
      let bd = Infinity;
      for (const c of p.cells) {
        const d = Math.hypot(c[0] + 0.5 - cx, c[1] + 0.5 - cy);
        if (d < bd) { bd = d; best = c; }
      }
      return { x: best[0] + 0.5, y: best[1] + 0.5, n: p.cells.length };
    });
  }, [analysis]);

  const n = analysis.pieces.length;
  const status = analysis.solved
    ? spec.ways <= 1
      ? `Yes! ${spec.pieces} identical pieces. It turns out this is the only way.`
      : found.length >= spec.ways
        ? `Yes! And you have found every way: all ${spec.ways} of them.`
        : `Yes! ${spec.pieces} identical pieces. Ways found: ${found.length} of ${spec.ways}. Clear the cuts and look for a different one.`
    : cuts.length === 0
      ? "Drag along the grid lines to cut. Tap a cut again to undo it."
      : n < spec.pieces
        ? `${n === 1 ? "Still one piece" : n + " pieces so far"}. You need ${spec.pieces} that all look alike.`
        : n > spec.pieces
          ? `${n} pieces is too many: you need exactly ${spec.pieces}.`
          : analysis.classes === 1
            ? "Almost!"
            : `${spec.pieces} pieces, but they don't all match yet. Matching pieces share a colour.`;

  return (
    <div className="board cut-board">
      <div className={"paper-card" + (analysis.solved ? " is-solved" : "")}>
        <svg
          ref={svgRef}
          viewBox={vb}
          className="cut-svg"
          role="application"
          tabIndex={0}
          aria-roledescription="cutting board"
          aria-label={`${spec.title}. Keyboard: arrow keys move between squares, Shift and an arrow key cuts or uncuts the wall on that side, Backspace undoes.`}
          onKeyDown={onKey}
          onFocus={() => { if (!cursor) setCursor(spec.cells[0]); }}
          onBlur={() => setCursor(null)}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onPointerLeave={() => setHover(null)}
          style={{ touchAction: "none", maxWidth: `${Math.min(640, (b.w + pad * 2) * 104)}px` }}
        >
          {spec.cells.map(([x, y]) => {
            const info = pieceOfCell.get(cellKey(x, y));
            const color = info && n > 1 ? COLORS[info.cls % COLORS.length] : "#f4e9c9";
            return <rect key={cellKey(x, y)} x={x} y={y} width={1.004} height={1.004} fill={color} className="cell" />;
          })}
          {inner.map((e) => {
            const [x1, y1, x2, y2] = segOf(e);
            return <line key={e} x1={x1} y1={y1} x2={x2} y2={y2} className="grid-line" />;
          })}
          {showAnswer &&
            [...answerSet].map((e) => {
              const [x1, y1, x2, y2] = segOf(e);
              return <line key={"a" + e} x1={x1} y1={y1} x2={x2} y2={y2} className="answer-line" />;
            })}
          {boundary.map((e) => {
            const [x1, y1, x2, y2] = segOf(e);
            return <line key={e + "b"} x1={x1} y1={y1} x2={x2} y2={y2} className="wall" />;
          })}
          {cuts.map((e) => {
            const [x1, y1, x2, y2] = segOf(e);
            return <line key={e + "c"} x1={x1} y1={y1} x2={x2} y2={y2} className="wall cut" />;
          })}
          {showCursor && cursor && (
            <rect x={cursor[0] + 0.06} y={cursor[1] + 0.06} width={0.88} height={0.88} rx={0.1} className="cursor-cell" />
          )}
          {hover && !cutSet.has(hover) && (() => {
            const [x1, y1, x2, y2] = segOf(hover);
            return <line x1={x1} y1={y1} x2={x2} y2={y2} className="hover-line" />;
          })()}
          {hintLevel >= 1 &&
            labels.map((l, i) => (
              <text key={i} x={l.x} y={l.y} className="size-label" textAnchor="middle" dominantBaseline="central">
                {l.n}
              </text>
            ))}
        </svg>
      </div>
      <p className={"board-status" + (analysis.solved ? " good" : "")} aria-live="polite">{status}</p>
      <p className="sr-only" aria-live="assertive">{kbdNote}</p>
      <p className="kbd-hint">Keyboard: Tab to the board, arrows move, Shift + arrow cuts a wall, Backspace undoes.</p>
      <div className="board-tools">
        <Button size="small" onClick={undo} disabled={!past.current.length} aria-label="Undo"><Undo2 size={17} /> Undo</Button>
        <Button size="small" onClick={redo} disabled={!future.current.length} aria-label="Redo"><Redo2 size={17} /> Redo</Button>
        <Button size="small" onClick={clear} disabled={!cuts.length}><Eraser size={17} /> Clear cuts</Button>
      </div>
    </div>
  );
}
